import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Code2, Copy, Check, Zap, Globe, AlertTriangle } from "lucide-react";
import { useState } from "react";
import { useChatbotConfig } from "../../hooks/useChatbotConfig";
import { getWidgetScriptUrl } from "../../config/apiConfig";

export function ModernFrameworksTab() {
  const { config } = useChatbotConfig();
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const handleCopyCode = async (code: string, type: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(type);
    
    // Simple toast notification
    const toast = document.createElement('div');
    toast.textContent = `${type} code copied to clipboard!`;
    toast.style.cssText = 'position:fixed;top:20px;right:20px;background:#10B981;color:white;padding:12px 24px;border-radius:8px;z-index:9999;box-shadow:0 4px 12px rgba(0,0,0,0.15)';
    document.body.appendChild(toast);
    setTimeout(() => {
      document.body.removeChild(toast);
      setCopiedCode(null);
    }, 2000);
  };

  const isConfigured = Boolean(config.businessId && config.isEnabled);
  const scriptUrl = isConfigured && config.businessId ? getWidgetScriptUrl(config.businessId) : 'SAVE_CONFIG_FIRST';

  // Framework integration codes
  const frameworks = {
    react: {
      name: "React",
      icon: "⚛️",
      description: "Perfect for Create React App and React-based projects",
      code: `import { useEffect } from 'react';

function App() {
  useEffect(() => {
    // Load Wildlife Chatbot Widget
    const script = document.createElement('script');
    script.src = '${scriptUrl}';
    script.async = true;
    
    // Optional: Configure widget behavior
    window.WildlifeChatbot = {
      autoOpen: false,
      position: 'bottom-right'
    };
    
    document.head.appendChild(script);
    
    // Cleanup on unmount
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="App">
      {/* Your app content */}
    </div>
  );
}

export default App;`,
      tips: [
        "Add to your main App.tsx or App.jsx component",
        "Widget automatically inherits your saved dashboard settings",
        "Use cleanup function to prevent memory leaks",
        "Test in both development and production builds"
      ]
    },
    nextjs: {
      name: "Next.js",
      icon: "▲",
      description: "Server-side rendering with optimal performance",
      code: `import { useEffect } from 'react';
import Script from 'next/script';

export default function Layout({ children }) {
  return (
    <>
      {children}
      
      {/* Wildlife Chatbot - Method 1: Using Next.js Script component */}
      <Script
        src="${scriptUrl}"
        strategy="afterInteractive"
        onLoad={() => {
          console.log('Wildlife Chatbot loaded successfully!');
        }}
      />
    </>
  );
}

// Alternative Method 2: Using useEffect (if you need more control)
function AlternativeImplementation() {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = '${scriptUrl}';
    script.async = true;
    document.head.appendChild(script);
    
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);
  
  return null;
}`,
      tips: [
        "Use Next.js Script component for optimal loading",
        "Place in _app.js or layout component for site-wide availability",
        "Strategy 'afterInteractive' ensures good performance",
        "Works with both static and server-side rendering"
      ]
    },
    vue: {
      name: "Vue.js",
      icon: "🟢",
      description: "Vue 3 Composition API and Options API supported",
      code: `<!-- Vue 3 Composition API -->
<template>
  <div id="app">
    <!-- Your app content -->
  </div>
</template>

<script setup>
import { onMounted, onUnmounted } from 'vue';

let scriptElement = null;

onMounted(() => {
  // Load Wildlife Chatbot Widget
  scriptElement = document.createElement('script');
  scriptElement.src = '${scriptUrl}';
  scriptElement.async = true;
  
  // Optional configuration
  window.WildlifeChatbot = {
    autoOpen: false,
    position: 'bottom-right'
  };
  
  document.head.appendChild(scriptElement);
});

onUnmounted(() => {
  // Cleanup
  if (scriptElement && scriptElement.parentNode) {
    scriptElement.parentNode.removeChild(scriptElement);
  }
});
</script>

<!-- Vue 2 Options API Alternative -->
<script>
export default {
  mounted() {
    const script = document.createElement('script');
    script.src = '${scriptUrl}';
    script.async = true;
    document.head.appendChild(script);
    this.$chatbotScript = script;
  },
  beforeDestroy() {
    if (this.$chatbotScript?.parentNode) {
      this.$chatbotScript.parentNode.removeChild(this.$chatbotScript);
    }
  }
}
</script>`,
      tips: [
        "Works with both Vue 2 and Vue 3",
        "Use onMounted/onUnmounted for proper lifecycle management",
        "Add to your main App.vue or root component",
        "Script will be available across all routes"
      ]
    },
    angular: {
      name: "Angular",
      icon: "🅰️",
      description: "Angular 12+ with TypeScript support",
      code: `// app.component.ts
import { Component, OnInit, OnDestroy } from '@angular/core';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {
  private chatbotScript: HTMLScriptElement | null = null;

  ngOnInit(): void {
    this.loadChatbotWidget();
  }

  ngOnDestroy(): void {
    // Cleanup
    if (this.chatbotScript?.parentNode) {
      this.chatbotScript.parentNode.removeChild(this.chatbotScript);
    }
  }

  private loadChatbotWidget(): void {
    // Create script element
    this.chatbotScript = document.createElement('script');
    this.chatbotScript.src = '${scriptUrl}';
    this.chatbotScript.async = true;
    
    // Optional configuration
    (window as any).WildlifeChatbot = {
      autoOpen: false,
      position: 'bottom-right'
    };
    
    // Add to document
    document.head.appendChild(this.chatbotScript);
    
    console.log('Wildlife Chatbot widget loaded');
  }
}

// Alternative: Using Angular Service
// chatbot.service.ts
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ChatbotService {
  private isLoaded = false;

  loadWidget(): Promise<void> {
    if (this.isLoaded) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = '${scriptUrl}';
      script.async = true;
      script.onload = () => {
        this.isLoaded = true;
        resolve();
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }
}`,
      tips: [
        "Implement in your main AppComponent for site-wide availability",
        "Use OnInit/OnDestroy for proper lifecycle management",
        "Consider creating a dedicated ChatbotService for reusability",
        "TypeScript types available for better development experience"
      ]
    },
    svelte: {
      name: "Svelte/SvelteKit",
      icon: "🧡",
      description: "Lightweight integration for Svelte applications",
      code: `<!-- App.svelte or +layout.svelte for SvelteKit -->
<script>
  import { onMount, onDestroy } from 'svelte';
  
  let chatbotScript;
  
  onMount(() => {
    // Load Wildlife Chatbot Widget
    chatbotScript = document.createElement('script');
    chatbotScript.src = '${scriptUrl}';
    chatbotScript.async = true;
    
    // Optional configuration
    window.WildlifeChatbot = {
      autoOpen: false,
      position: 'bottom-right'
    };
    
    document.head.appendChild(chatbotScript);
  });
  
  onDestroy(() => {
    // Cleanup
    if (chatbotScript?.parentNode) {
      chatbotScript.parentNode.removeChild(chatbotScript);
    }
  });
</script>

<!-- Your app content -->
<main>
  <slot />
</main>

<!-- SvelteKit specific: app.html -->
<!-- Add this to src/app.html if you prefer -->
<script>
  // This runs on every page load
  const script = document.createElement('script');
  script.src = '${scriptUrl}';
  script.async = true;
  document.head.appendChild(script);
</script>`,
      tips: [
        "Use onMount/onDestroy for component lifecycle",
        "For SvelteKit, add to +layout.svelte for site-wide availability",
        "Can also add directly to app.html for simpler setup",
        "Works with both Svelte and SvelteKit applications"
      ]
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center p-6 rounded-xl bg-gradient-to-br from-emerald-50 to-purple-50 border border-blue-200/60">
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Modern Framework Integration</h2>
        <p className="text-slate-600">Copy-paste examples for React, Vue, Angular, Next.js, and more.</p>
        
        {isConfigured ? (
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 px-4 py-2 mt-4">
            Configuration Ready
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 px-4 py-2 mt-4">
            Save Configuration First
          </Badge>
        )}
      </div>

      {/* React Example */}
      <div className="p-6 rounded-lg border border-slate-200 bg-white">
        <h3 className="text-lg font-semibold mb-4">⚛️ React Integration</h3>
        <pre className="text-xs bg-slate-900 text-slate-100 p-4 rounded overflow-x-auto">
          <code>
{`import { useEffect } from 'react';

function App() {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = '${scriptUrl}';
    script.async = true;
    document.head.appendChild(script);
    
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return <div className="App">{/* Your content */}</div>;
}`}
          </code>
        </pre>
      </div>

      {/* Next.js Example */}
      <div className="p-6 rounded-lg border border-slate-200 bg-white">
        <h3 className="text-lg font-semibold mb-4">▲ Next.js Integration</h3>
        <pre className="text-xs bg-slate-900 text-slate-100 p-4 rounded overflow-x-auto">
          <code>
{`import Script from 'next/script';

export default function Layout({ children }) {
  return (
    <>
      {children}
      <Script
        src="${scriptUrl}"
        strategy="afterInteractive"
      />
    </>
  );
}`}
          </code>
        </pre>
      </div>

      {/* Vue Example */}
      <div className="p-6 rounded-lg border border-slate-200 bg-white">
        <h3 className="text-lg font-semibold mb-4">🟢 Vue.js Integration</h3>
        <pre className="text-xs bg-slate-900 text-slate-100 p-4 rounded overflow-x-auto">
          <code>
{`<script setup>
import { onMounted } from 'vue';

onMounted(() => {
  const script = document.createElement('script');
  script.src = '${scriptUrl}';
  script.async = true;
  document.head.appendChild(script);
});
</script>`}
          </code>
        </pre>
      </div>

      {/* Framework Selection */}
      <Tabs defaultValue="react" className="space-y-6">
        <TabsList className="grid grid-cols-3 lg:grid-cols-5 gap-2 bg-slate-50/80 p-2 rounded-lg shadow-sm border border-slate-200/60 h-auto">
          {Object.entries(frameworks).map(([key, framework]) => (
            <TabsTrigger 
              key={key}
              value={key} 
              className="flex flex-col items-center gap-1 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 p-3 rounded-md text-sm h-auto min-h-[60px]"
            >
              <span className="text-lg">{framework.icon}</span>
              <span className="font-medium">{framework.name}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Framework Content */}
        {Object.entries(frameworks).map(([key, framework]) => (
          <TabsContent key={key} value={key} className="space-y-6">
            <Card className="border border-slate-200/60 shadow-md shadow-slate-900/5">
              <CardHeader className="pb-4 border-b border-slate-100/80">
                <CardTitle className="flex items-center gap-3">
                  <span className="text-2xl">{framework.icon}</span>
                  <div>
                    <div className="text-xl font-semibold text-slate-900">{framework.name} Integration</div>
                    <div className="text-sm text-slate-600 font-normal">{framework.description}</div>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Code Block */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-slate-900">Implementation Code</h4>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCopyCode(framework.code, framework.name)}
                      className="shadow-sm"
                    >
                      {copiedCode === framework.name ? (
                        <>
                          <Check className="w-4 h-4 mr-2 text-green-600" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Code
                        </>
                      )}
                    </Button>
                  </div>
                  
                  <div className="relative">
                    <pre className="text-xs bg-slate-900 text-slate-100 p-6 rounded-lg overflow-x-auto border border-slate-700/50 shadow-lg">
                      <code className="text-green-400">
                        {framework.code}
                      </code>
                    </pre>
                  </div>
                  
                  {/* Implementation Tips */}
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h5 className="font-medium text-blue-900 mb-2">💡 Implementation Tips:</h5>
                    <ul className="text-sm text-blue-800 space-y-1">
                      {framework.tips.map((tip, index) => (
                        <li key={index}>• {tip}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Configuration Not Ready Warning */}
      {!isConfigured && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Configuration Required:</strong> Please save your chatbot configuration first to get the correct embed URL in the code examples above.
          </AlertDescription>
        </Alert>
      )}

      {/* Universal Benefits */}
      <div className="p-6 rounded-lg border border-emerald-200/60 bg-emerald-50/50">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5 text-emerald-600" />
          Why Our Integration Is Better
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <h4 className="font-medium text-emerald-900">🎯 Smart & Simple</h4>
            <ul className="text-sm text-emerald-800 space-y-1">
              <li>• No manual configuration needed</li>
              <li>• Auto-loads your dashboard settings</li>
              <li>• Updates instantly when you change settings</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="font-medium text-emerald-900">⚡ Performance Optimized</h4>
            <ul className="text-sm text-emerald-800 space-y-1">
              <li>• Async loading for non-blocking performance</li>
              <li>• Minimal bundle size impact</li>
              <li>• Works with SSR and static generation</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
} 